<template>
    <div class="sidebar-tool" :class="{ active: isMenuActive(), 'mobile-sidebar': isMobile }">
        <div class="sidebar-content" @mouseenter="mouseEnterStart" @mouseleave="mouseLeaveStart">
            <div class="sidebar-menu" v-if="cloneItems.length > 0">
                <div class="sidebar-menu-content">
                    <template v-for="item of cloneItems">
                        <a-tooltip
                            :title="item.label"
                            placement="right"
                            :key="item.key"
                            :overlayClassName="isMenuActive() ? '' : 'hiddenToolTip'"
                        >
                            <router-link v-if="item.route" :to="item.route" class="menu-item">
                                <SidebarChildItem
                                    :item="$_.merge({}, item, { label: formatMenuLabel(item.label) })"
                                    :isSelected="isSelected"
                                    :isMenuActive="isMenuActive"
                                />
                            </router-link>
                            <SidebarChildItem
                                v-else
                                :item="$_.merge({}, item, { label: formatMenuLabel(item.label) })"
                                :isSelected="isSelected"
                                :isMenuActive="isMenuActive"
                                @selected="fnCallback"
                                @clicked="fnCallback"
                            />
                        </a-tooltip>
                    </template>
                </div>
            </div>
            <div class="sidebar-grouped" v-for="groupItem of groupedItems">
                <a-tooltip
                    :title="groupItem.name"
                    placement="right"
                    :key="groupItem.name"
                    :overlayClassName="isMenuActive() ? '' : 'hiddenToolTip'"
                >
                    <div class="sidebar-grouped-head" @click="toggleSubMenu(groupItem)">
                        <div class="sidebar-grouped-head-title">{{ groupItem.name }}</div>
                        <div class="sidebar-grouped-head-collapse">
                            <div class="material-icons">{{ groupItem.showMore ? "expand_less" : "expand_more" }}</div>
                        </div>
                    </div>
                </a-tooltip>
                <div class="sidebar-menu" :class="{ closed: !groupItem.showMore }">
                    <div class="sidebar-menu-content">
                        <template v-for="item of groupItem.items">
                            <a-tooltip
                                :title="item.label"
                                placement="right"
                                :key="item.key"
                                :overlayClassName="isMenuActive() ? '' : 'hiddenToolTip'"
                            >
                                <router-link v-if="item.route" :to="item.route">
                                    <SidebarChildItem
                                        :item="item"
                                        :isSelected="isSelected"
                                        :isMenuActive="isMenuActive"
                                    />
                                </router-link>
                                <SidebarChildItem
                                    v-else
                                    :item="item"
                                    :isSelected="isSelected"
                                    :isMenuActive="isMenuActive"
                                    @selected="fnCallback"
                                />
                            </a-tooltip>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <div class="sidebar-tool-collapse" @click="toggle">
            <div class="information-text">{{ $t("message.showHide") }}</div>
            <div>
                <div class="material-icons">{{ isMenuActive() ? "chevron_left" : "chevron_right" }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import SidebarChildItem from "./SidebarChildItem.vue";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
    name: "SidebarChild",
    components: { SidebarChildItem },
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        selectedItem: {
            type: [String, Number],
            default: null,
        },
        focusActive: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState({
            isMobile: (state) => state.app.isMobile,
            menu: (state) => state.layout.menu,
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
    },
    data() {
        return {
            isActive: false,
            groupedItems: [],
            cloneItems: [],
            selectedItemClone: null,
            hoverActive: false,
            hoverTimerId: null,
            isSidebarBeforeActive: false,
        };
    },
    methods: {
        ...mapActions({
            toggleMenu: "layout/toggleMenu",
        }),
        mouseEnterStart() {
            this.hoverTimerId = setTimeout(() => {
                this.hoverActive = true;
                this.isSidebarBeforeActive = this.menu.isOpen;
                this.toggleMenu(false);
            }, 250);
        },
        mouseLeaveStart() {
            if (this.hoverTimerId) clearTimeout(this.hoverTimerId);
            this.hoverActive = false;
            setTimeout(() => {
                if (this.isSidebarBeforeActive) {
                    this.toggleMenu(true);
                }
            }, 250);
        },
        isSidebarMenu() {
            return this.menu.isOpen || this.menu.hoverActive;
        },
        isMenuActive() {
            return !this.isSidebarMenu() && (this.isActive || (this.focusActive && this.hoverActive));
        },
        toggle() {
            this.isActive = !this.isActive;
            if (this.isActive) {
                this.isSidebarBeforeActive = this.menu.isOpen;
                this.toggleMenu(false);
            } else if (this.isSidebarBeforeActive) {
                this.toggleMenu(true);
            }
        },
        toggleSubMenu(group) {
            group.showMore = !group.showMore;
        },
        isSelected(item) {
            const selectedItem = this.items.find(
                (i) => i.key === this.selectedItemClone || i.parentKey === this.selectedItemClone
            );
            return item && selectedItem && (item.key === selectedItem.key || item.key === selectedItem.parentKey);
        },
        fnCallback(item) {
            if (item.clickEvent) {
                this.$emit("clicked", item);
            } else {
                this.selectedItemClone = item?.parentKey || item.key;
                this.$emit("selected", item);
            }
            if (item.path) {
                this.$router.push(item.path);
            }
        },
        initData() {
            let items = this.$_.cloneDeep(this.items);
            this.selectedItemClone = this.selectedItem || items[0]?.key;
            items = items.reduce(
                (acc, item) => {
                    if (!item.enabled) return acc;
                    if (item.group) {
                        acc.grouped[item.group] = {
                            name: item.group,
                            showMore: true,
                            items: [...(acc.grouped[item.group]?.items || []), item],
                        };
                    } else {
                        acc.noGrouped.push(item);
                    }
                    return acc;
                },
                {
                    grouped: {},
                    noGrouped: [],
                }
            );
            this.groupedItems = items.grouped;
            this.cloneItems = items.noGrouped;
        },
        formatMenuLabel(label) {
            if (!label) return "";
            const maxLength = 25;
            return label.length > maxLength ? `${label.substring(0, maxLength)}...` : label;
        },
    },
    mounted() {
        this.initData();
    },
    watch: {
        items: {
            deep: true,
            handler() {
                this.initData();
            },
        },
        selectedItem() {
            if (this.selectedItemClone !== this.selectedItem) {
                this.selectedItemClone = this.selectedItem;
            }
        },
        menu: {
            handler(newVal) {
                if (newVal.isOpen || newVal.hoverActive) {
                    this.hoverActive = false;
                }
            },
            deep: true,
        },
    },
};
</script>
<style lang="scss">
.hiddenToolTip {
    opacity: 0 !important;
    display: none !important;
}

#contents.mobile-contents {
    .sidebar-tool {
        position: absolute !important;
        height: calc(100vh - 56px);
    }
}
</style>
<style lang="scss" scoped>
.sidebar-tool {
    width: 60px;
    border-right: 1px solid var(--color-border-default);
    height: 100%;
    position: relative;
    transition: all 0.2s;
    user-select: none;
    min-width: 60px;
    z-index: 99;

    .sidebar-content {
        height: calc(100% - 40px);
        overflow: auto;
        background: var(--color-bg-default);

        &::-webkit-scrollbar {
            display: none;
        }
    }

    &.active {
        width: 240px;
        min-width: 240px;

        .sidebar-menu-item {
            .sidebar-menu-item-content {
                display: flex !important;
                align-items: center !important;
                justify-content: space-between;
                width: 100% !important;
            }
        }

        .sidebar-grouped {
            .sidebar-grouped-head {
                justify-content: space-between;

                .sidebar-grouped-head-title {
                    display: block !important;
                }
            }
        }

        .sidebar-tool-collapse {
            padding: 0 10px;
            justify-content: space-between;

            .information-text {
                display: block !important;
            }
        }
    }

    .sidebar-menu {
        transition: all 0.2s;
        max-height: 800px; // 10 item
        overflow: hidden;
        opacity: 1;

        &.closed {
            max-height: 0;
            opacity: 0;
        }
    }

    .sidebar-menu-content {
        padding: 11px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
        line-height: 0;
        background: var(--color-bg-default) !important;

        a {
            width: 100%;
            color: black;
        }
    }

    .sidebar-grouped {
        width: 100%;
        display: flex;
        flex-direction: column;

        .sidebar-grouped-head {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            border: var(--color-border-default);
            border-style: solid;
            border-width: 1px 0;
            border-bottom-width: 0;
            padding: 7px 11px;
            cursor: pointer;

            .sidebar-grouped-head-title {
                display: none;
                white-space: nowrap;
                width: 100%;
                line-height: 0;
            }

            .sidebar-grouped-head-collapse {
                line-height: 0;

                .material-icons {
                    font-size: 16px;
                }
            }
        }
    }

    .sidebar-tool-collapse {
        position: absolute;
        border-top: 1px solid var(--color-border-default);
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        cursor: pointer;
        user-select: none;
        background: var(--color-bg-default) !important;
        height: 40px;
        color: var(--default-text-color);

        .information-text {
            display: none;
            white-space: nowrap;
        }
    }
}

.menu-item {
    display: block;
    width: 100%;
    text-decoration: none;

    .menu-item-label {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 150px;
    }
}
</style>
